import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { 
  FaPlane,
  FaHotel, 
  FaUmbrellaBeach,
  FaSwimmingPool,
  FaMountain,
  FaSun,
  FaBed,
  FaCompass,
  FaGlobe,
  FaCamera
} from "react-icons/fa";

// CSS
import "./customloading.css";

const CustomLoading = ({
  type = "spinner",
  size = "medium",
  color = "primary",
  label = "",
  className = "",
  overlay = false,
  fullScreen = false,
  centered = true,
}) => {
  const hasExplicitLabel = typeof label === "string" && label.trim() !== "";
  // Random messages for loading
  const loadingMessages = [
    "טוען הצעות מלונות מהספקים... רגע סוכן, זה שווה המתנה.",
    "מבצע השוואת מחירים מול ספקים בינלאומיים...",
    "טוען זמינות ומחירים בזמן אמת — אל תתרחק רחוק מדי",
    "בודק עבורך את ההצעות הטובות ביותר ללקוח שלך...",
    "מתחבר למנועי חיפוש מלונות...",
    "מאחד נתונים ממקורות שונים — חכה שנייה, זה קורה!",
    "מבצע סנכרון מול הספקים — אל תמהר לסגור חלון",
    "מנוע HotelsRiver מחשב את התוצאות בזמן אמת...",
    "מחפש את הדיל שהמתחרים לא מצאו...",
    "HotelsRiver עובד בשבילך — תוצאות בדרך!",
    "רגע סוכן, המערכת בודקת מאות הצעות...",
    "חכה רגע, אנחנו מנווטים בין הספקים בשבילך...",
    "HotelsRiver בזרימה... טוענים את ההצעות שלך",
    "בודקים זמינות מכל היעדים — המערכת עובדת בשבילך.",
    "טוענים מלונות, תכף תראה את המסך שאתה הכי אוהב",
    "סוכן טוב יודע לחכות כמה שניות בשביל הצעה שווה באמת"
  ];

  // 10 selected tourism icons
  const tourismIcons = [
    FaPlane,           // מטוס
    FaHotel,           // מלון
    FaUmbrellaBeach,   // חוף
    FaSwimmingPool,    // בריכה
    FaMountain,        // הרים
    FaSun,             // שמש
    FaBed,             // מיטה
    FaCompass,         // מצפן
    FaGlobe,           // כדור הארץ
    FaCamera           // מצלמה
  ];

  const [currentMessage, setCurrentMessage] = useState(() => {
    // Initialize with a random message immediately
    return loadingMessages[Math.floor(Math.random() * loadingMessages.length)];
  });
  const [currentIconIndex, setCurrentIconIndex] = useState(() => {
    // Start with plane icon (index 0)
    return 0;
  });
  const [iconKey, setIconKey] = useState(0);

  useEffect(() => {
    if (hasExplicitLabel) {
      return undefined;
    }
    // Change icon every 1.5 seconds for faster rotation
    const iconInterval = setInterval(() => {
      const newIndex = Math.floor(Math.random() * tourismIcons.length);
      setCurrentIconIndex(newIndex);
      setIconKey(prev => prev + 1); // Force re-render with animation
    }, 1500);

    // Change message every 3 seconds (always, even if label is provided)
    const messageInterval = setInterval(() => {
      const newMessage = loadingMessages[Math.floor(Math.random() * loadingMessages.length)];
      setCurrentMessage(newMessage);
    }, 3000);

    return () => {
      clearInterval(iconInterval);
      clearInterval(messageInterval);
    };
  }, [hasExplicitLabel]);

  const messageToDisplay = hasExplicitLabel ? label : currentMessage;
  const canPortal =
    typeof document !== "undefined" && document.body;

  useEffect(() => {
    if (!import.meta.env.DEV || typeof window === "undefined") return;
    const pathname = window.location.pathname || "";
    const category =
      new URLSearchParams(window.location.search).get("category") || null;
    const isHotelRoute =
      /hotel|package|vacation-packages|searchoffer/i.test(pathname) ||
      category === "hotels" ||
      category === "packages";
    const loadingFlag = Boolean(overlay || fullScreen);
    const shouldShowHotelLoader =
      loadingFlag && !hasExplicitLabel && isHotelRoute;
    }, [overlay, fullScreen, hasExplicitLabel, label, messageToDisplay]);

  const renderIconAnimation = () => {
    // Ensure currentIconIndex is valid
    const iconIndex = currentIconIndex >= 0 && currentIconIndex < tourismIcons.length 
      ? currentIconIndex 
      : 0;
    const IconComponent = tourismIcons[iconIndex];
    
    if (!IconComponent) {
      return (
        <div className={`loading-icon-animation loading-${size} loading-${color}`}>
          <div className="icon-circle" key={iconKey}>
            <div className="tourism-icon-fallback">?</div>
          </div>
        </div>
      );
    }
    
    // Use React.createElement to ensure proper rendering
    try {
      return (
        <div className={`loading-icon-animation loading-${size} loading-${color}`}>
          <div className="icon-circle" key={iconKey}>
            {React.createElement(IconComponent, { className: "tourism-icon" })}
          </div>
          <div className="icon-particles">
            <div className="particle particle-1"></div>
            <div className="particle particle-2"></div>
            <div className="particle particle-3"></div>
          </div>
        </div>
      );
    } catch (error) {
      console.error('Error rendering icon:', error);
      return (
        <div className={`loading-icon-animation loading-${size} loading-${color}`}>
          <div className="icon-circle" key={iconKey}>
            <div className="tourism-icon-fallback">?</div>
          </div>
        </div>
      );
    }
  };

  const renderBattery = () => {
    // Battery rendering kept for compatibility
    return renderIconAnimation();
  };

  const renderSpinner = () => {
    switch (type) {
      case "dots":
        return (
          <div className={`loading-dots loading-${size}`}>
            <div className="dot"></div>
            <div className="dot"></div>
            <div className="dot"></div>
          </div>
        );

      case "pulse":
        return <div className={`loading-pulse loading-${size}`}></div>;

      case "ripple":
        return (
          <div className={`loading-ripple loading-${size}`}>
            <div></div>
            <div></div>
          </div>
        );

      case "bars":
        return (
          <div className={`loading-bars loading-${size}`}>
            <div className="bar"></div>
            <div className="bar"></div>
            <div className="bar"></div>
            <div className="bar"></div>
          </div>
        );

      case "ring":
        return <div className={`loading-ring loading-${size}`}></div>;

      case "battery":
        return renderBattery();

      case "icons":
        return renderIconAnimation();

      default:
        return renderIconAnimation(); // Default to icon animation
    }
  };

  const loadingContent = (
    <div
      className={`custom-loading ${className} loading-${color} ${
        fullScreen ? "loading-fullscreen" : ""
      } ${centered ? "loading-centered" : ""}`}
    >
      <div className="loading-content">
        <div className="loading-icon-wrapper">
          {renderSpinner()}
        </div>
        {messageToDisplay && (
          <span className="loading-label" key={messageToDisplay}>
            {messageToDisplay}
          </span>
        )}
      </div>
    </div>
  );

  // If centered is true and not overlay/fullScreen, wrap in centered container
  if (centered && !overlay && !fullScreen) {
    const centeredContent = (
      <div className="loading-center-wrapper">{loadingContent}</div>
    );
    return canPortal ? createPortal(centeredContent, document.body) : centeredContent;
  }

  if (overlay || fullScreen) {
    const overlayContent = (
      <div
        className={`loading-overlay ${fullScreen ? "loading-fullscreen" : ""}`}
      >
        {loadingContent}
      </div>
    );
    return canPortal ? createPortal(overlayContent, document.body) : overlayContent;
  }

  return loadingContent;
};

CustomLoading.propTypes = {
  type: PropTypes.oneOf(["spinner", "dots", "pulse", "ripple", "bars", "ring", "battery", "icons"]),
  size: PropTypes.oneOf(["small", "medium", "large"]),
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "success",
    "warning",
    "error",
  ]),
  label: PropTypes.string,
  className: PropTypes.string,
  overlay: PropTypes.bool,
  fullScreen: PropTypes.bool,
  centered: PropTypes.bool,
};

export default CustomLoading;
